import { Box, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';

interface CustomPercentageInputProps {
  value: number;
  onChange: ((value: any) => void) | (() => void);
  disabled?: boolean;
  fontWeight?: number;
  width?: number;
  name?: string;
  id?: string;
  /**
   * Placeholder shown when the underlying input value is empty. Used by the
   * salary-loading mode in TransactionActivityAllocation to surface "auto" in
   * the disabled cells.
   */
  placeholder?: string;
  /**
   * When true, the displayed value is forced empty (the local state ignores
   * `value` and renders only the placeholder). Used together with `disabled`
   * and `placeholder="auto"` for the salary-loading mode.
   */
  forceEmpty?: boolean;
}

export const CustomPercentageInput = memo<CustomPercentageInputProps>(
  ({
    value,
    onChange,
    disabled = false,
    fontWeight = 400,
    width = 80,
    name,
    id,
    placeholder,
    forceEmpty = false,
  }) => {
    const [localValue, setLocalValue] = useState(forceEmpty ? '' : value.toString());

    useEffect(() => {
      setLocalValue(forceEmpty ? '' : value.toString());
    }, [value, forceEmpty]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value.replace(/[^\d.]/g, '');
      setLocalValue(v);

      const numericValue = parseFloat(v) || 0;

      // Check if onChange expects a parameter by checking the function length
      if (onChange.length > 0) {
        (onChange as (value: any) => void)(numericValue);
      } else {
        (onChange as () => void)();
      }
    };

    return (
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          width: `${width}px`,
        }}
      >
        <input
          type="text"
          inputMode="numeric"
          name={name}
          id={id}
          value={localValue}
          placeholder={placeholder}
          onChange={handleChange}
          disabled={disabled}
          style={{
            width: '100%',
            height: '36px',
            textAlign: 'center',
            fontSize: '0.75rem',
            fontFamily: 'inherit',
            fontWeight,
            borderRadius: '6px',
            border: '1px solid #d1d5db',
            backgroundColor: '#ffffff',
            padding: '8.5px 14px',
            paddingRight: '28px',
            transition: 'border-color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onFocus={(e) => {
            (e.target as HTMLInputElement).style.borderColor = '#4f46e5'; // matches theme primary.main
            (e.target as HTMLInputElement).style.borderWidth = '1px'; // theme uses 1px, not 2px
          }}
          onBlur={(e) => {
            (e.target as HTMLInputElement).style.borderColor = '#d1d5db';
            (e.target as HTMLInputElement).style.borderWidth = '1px';
          }}
          onMouseEnter={(e) => {
            if (!disabled) {
              (e.target as HTMLInputElement).style.borderColor = '#9ca3af'; // matches theme hover color
            }
          }}
          onMouseLeave={(e) => {
            if (!disabled && e.target !== document.activeElement) {
              (e.target as HTMLInputElement).style.borderColor = '#d1d5db';
            }
          }}
        />
        {!forceEmpty && (
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'text.secondary', // uses theme color
              pointerEvents: 'none',
              userSelect: 'none',
              fontSize: '0.75rem', // matches theme typography.caption.fontSize
            }}
          >
            %
          </Typography>
        )}
      </Box>
    );
  }
);

CustomPercentageInput.displayName = 'CustomPercentageInput';
export default CustomPercentageInput;
